import { PartnerItem, PartnersApi } from '@deckee/api-app-main';
import { Map, MapboxGeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import apiConfiguration from '../../backend/apiConfiguration';
import { captureEvent } from '../../event-capture';
import { transitionToRawURL } from '../../route/route-navigation';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

const partnersApi = new PartnersApi(apiConfiguration);
let mapReference: Map | null = null;
const layerName = 'partnerMarker';

const addLayersIfRequired = () => {
  if (!mapReference) return;

  if (!sourceExists(mapReference, layerName)) {
    mapReference.addSource(layerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }

  if (!layerExists(mapReference, layerName)) {
    mapReference.addLayer({
      id: layerName,
      type: 'symbol',
      source: layerName,
      layout: {
        'icon-image': ['get', 'image'],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 2.9999, 0, 3, 0.6],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer: any = {
  synonyms: 'partner',
  displayName: 'Partner',
  group: 'standardMarker',
  typeUrl: 'partner',

  shownByDefault: true,
  relativeDepth: 100,
  menuOptions: {
    visible: false,
  },
  // NOTE: this isn't needed, except it fails validation without it :'(
  standardMarkerOptions: {
    markerType: 'partnerMarker',
    allowCreation: false,
    allowEditing: false,
    allowComments: false,
    viewOptions: {
      typeTitle: 'Partner',
      itemTitleFunction: (item: PartnerItem) =>
        (item && item.name) || 'Partner',
    },
    creationOptions: {
      menuText: 'Partner',
      newTitle: 'Partner',
      titlePlaceholder: 'Partner',
      checkBoxItems: [],
      markerTypeDescription: null,
    },
    editingOptions: {},
  },
  icon: {
    baseShape: null,
    iconColor: '#fff',
    markerColor: '#906BD9',
  },
};

const loadMarkerImages = async (imageUrls: string[], mapReference: Map) => {
  const promiseArray = imageUrls.map(async (imageId) => {
    if (imageId && !mapReference.hasImage(imageId)) {
      mapReference.loadImage(imageId, (error: any, image: any) => {
        if (error) throw error;
        mapReference.addImage(imageId, image);
      });
    }
    return true;
  });
  await Promise.all(promiseArray);
};

layer.onInitialize = async (map: Map) => {
  mapReference = map;
  addLayersIfRequired();

  const { data } = await partnersApi.getPartners({
    topLeftLatitude: '90',
    topLeftLongitude: '-180',
    bottomRightLatitude: '-90',
    bottomRightLongitude: '180',
  });

  const partnersWithMarkerConfig = data.filter(
    (item) =>
      item?.markerConfigJson?.imageUrl &&
      item?.markerConfigJson?.latitude &&
      item?.markerConfigJson?.longitude,
  );

  await loadMarkerImages(
    partnersWithMarkerConfig.map(
      (item) => item.markerConfigJson?.imageUrl as string,
    ),
    mapReference,
  );

  const geojson = {
    type: 'FeatureCollection',
    features: partnersWithMarkerConfig.map((item) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          item?.markerConfigJson?.longitude,
          item?.markerConfigJson?.latitude,
        ],
      },
      properties: {
        ...item,
        image: item?.markerConfigJson?.imageUrl,
      },
    })),
  };

  setDataSource(mapReference, layerName, geojson);

  map.on(
    'click',
    layerName,
    async (
      clickEvent: MapMouseEvent & { features?: MapboxGeoJSONFeature[] },
    ) => {
      if (!clickEvent.features?.[0]?.properties) return;

      const properties = clickEvent.features[0].properties as PartnerItem;
      const { id, slug } = properties;

      if (!clickEvent.originalEvent.defaultPrevented) {
        clickEvent.originalEvent.preventDefault();

        captureEvent({
          type: 'marker: click',
          payload: {
            markerType: 'partnerMarker',
            id,
            slug,
            clickedLatitude: clickEvent.lngLat.lat,
            clickedLongitude: clickEvent.lngLat.lng,
          },
        });
        transitionToRawURL(`/maps/partner/${slug}`);
      }
    },
  );
};

layer.onToggle = (map: Map, show: boolean) => {
  toggleLayerIfRequired(map, layerName, show);
};

export default layer;
